import { buildListQueryParams, processListPage } from '@/lib/api/listQuery';
import { listFilter } from '@/lib/mappers/filters';
import { formatDate } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import { ACCESS_STATUS, accessStatusLabel } from '../constants/accessStatus';
import { activityModuleLabel, toActivityModules } from '../constants/activityModules';
import {
  ACTIVITY_FILTERS,
  ACTIVITY_SORT_FIELDS,
  ADMIN_DATE_TIME_FORMAT,
  LAST_ACTIVE_PRESETS,
  USER_FIELDS,
  USER_FILTERS,
  USER_SEARCH_IN,
  USER_SORT_FIELDS,
} from '../constants/admin.constants';
import {
  USER_ROLE,
  roleHasAllSites,
  rolePicksManySites,
  rolePicksOneSite,
  userRoleLabel,
} from '../constants/userRoles';
import { resolveActivityLink } from '../utils/activityLinks';

// DTO ⇄ view model for /access-manager/users, /auth/invite-user and /activity-logs.

const formatDateTimeOrDash = (value) => formatDate(value, ADMIN_DATE_TIME_FORMAT) || '-';

const toJsonParam = (value) => (Object.keys(value).length ? JSON.stringify(value) : undefined);

const withParam = (params, key, value) =>
  value === undefined ? params : { ...params, [key]: value };

// A date group is `{ preset }` or `{ range, from, to }`; the server 400s a half-open range.
const toDateWindow = (value) => {
  if (value?.preset) return { preset: value.preset };
  if (value?.from && value?.to) return { range: [value.from, value.to] };
  return {};
};

// TODO: a lastactive filter to be added later — today / yesterday / dateRange match updated_at.
const buildLastActiveFilter = (value) => {
  const { preset, range } = toDateWindow(value);
  if (preset === LAST_ACTIVE_PRESETS.today) return { today: true };
  if (preset === LAST_ACTIVE_PRESETS.yesterday) return { yesterday: true };
  return range ? { dateRange: range } : {};
};

/** useTableState → GET /access-manager/users params (queryHandlerMiddleware, 0-indexed page). */
export const buildUsersQueryParams = (state = {}) => {
  const params = buildListQueryParams(state, {
    sortFields: USER_SORT_FIELDS,
    searchIn: USER_SEARCH_IN,
  });
  const role = listFilter(state[USER_FILTERS.accessType]);
  const filters = {
    ...(role ? { role } : {}),
    ...buildLastActiveFilter(state[USER_FILTERS.lastActive]),
  };
  return withParam(params, 'filters', toJsonParam(filters));
};

const fullName = (dto) => [dto.firstname, dto.lastname].filter(Boolean).join(' ').trim();

const toAccessEntry = (id, name, code) => ({
  id: String(id),
  name: formatTextOrDash(name),
  code: formatTextOrDash(code),
});

/** One Access manager row — a pending invitee has no name yet, so it reads as its email. */
export const processUserRow = (dto) => {
  const name = fullName(dto);
  return {
    id: dto.id,
    email: dto.email ?? '',
    displayName: name ? `${name} (${dto.email})` : formatTextOrDash(dto.email),
    role: dto.role ?? null,
    accessType: formatTextOrDash(userRoleLabel(dto.role)),
    hasAllSites: roleHasAllSites(dto.role),
    statusCode: dto.status ?? null,
    status: accessStatusLabel(dto.status) ?? '-',
    isPending: dto.status === ACCESS_STATUS.pendingInvitation,
    lastActive: formatDateTimeOrDash(dto.lastActive),
    sites: (dto.associatedSites ?? []).map((site) =>
      toAccessEntry(site.id, site.site_name, site.site_id),
    ),
    storages: (dto.associatedStorages ?? []).map((storage) =>
      toAccessEntry(storage.id, storage.storage_name, storage.storage_id),
    ),
  };
};

export const processUsersPage = (dto) => processListPage(dto, 'users', processUserRow);

/** The Sites & Storage units access modal's rows — sites first, then storage units. */
export const buildSiteAccessRows = (row) =>
  [
    ...(row?.sites ?? []).map((site) => ({ ...site, id: `site-${site.id}` })),
    ...(row?.storages ?? []).map((storage) => ({ ...storage, id: `storage-${storage.id}` })),
  ].map((entry, index) => ({ ...entry, serialNo: index + 1 }));

/** A list row → the Edit user form; the single-site field reads the first site. */
export const toUserFormValues = (row) => {
  const siteIds = (row?.sites ?? []).map((site) => site.id);
  return {
    [USER_FIELDS.email]: row?.email ?? '',
    [USER_FIELDS.role]: row?.role ?? '',
    [USER_FIELDS.siteIds]: siteIds,
    [USER_FIELDS.siteId]: siteIds[0] ?? '',
    [USER_FIELDS.storageIds]: (row?.storages ?? []).map((storage) => storage.id),
  };
};

const toIds = (values) => (values ?? []).filter(Boolean).map(Number);

// A super admin carries no site list server-side; the others send whichever site field is drawn.
const buildAccessIds = (values) => {
  const role = values[USER_FIELDS.role];
  if (roleHasAllSites(role)) return { associatedSiteIds: [], associatedStorageIds: [] };
  const siteIds = rolePicksOneSite(role)
    ? [values[USER_FIELDS.siteId]]
    : rolePicksManySites(role)
      ? values[USER_FIELDS.siteIds]
      : [];
  return {
    associatedSiteIds: toIds(siteIds),
    associatedStorageIds: toIds(values[USER_FIELDS.storageIds]),
  };
};

/** Form values → POST /auth/invite-user; a super admin invite omits the access lists. */
export const buildInviteDto = (values) => {
  const dto = {
    email: (values[USER_FIELDS.email] ?? '').trim().toLowerCase(),
    role: values[USER_FIELDS.role],
  };
  return values[USER_FIELDS.role] === USER_ROLE.superAdmin
    ? dto
    : { ...dto, ...buildAccessIds(values) };
};

/** Form values → PUT /access-manager/users/:id — role and both lists always travel together. */
export const buildUpdateUserDto = (values) => ({
  role: values[USER_FIELDS.role],
  ...buildAccessIds(values),
});

/** The Activity log's User filter options, from one page of the account's users. */
export const buildActivityUserOptions = (page) =>
  (page?.rows ?? []).map((row) => ({ value: String(row.id), label: row.displayName }));

const buildActivityFilters = (state) => {
  const { preset, range } = toDateWindow(state[ACTIVITY_FILTERS.activityDate]);
  const modules = toActivityModules(state[ACTIVITY_FILTERS.module]);
  const users = listFilter(state[ACTIVITY_FILTERS.user]);
  return {
    ...(preset ? { activityDate: [preset] } : {}),
    ...(range ? { dateRange: range } : {}),
    ...(modules.length ? { module: modules } : {}),
    ...(users ? { users: users.map(Number) } : {}),
  };
};

/** useTableState → GET /activity-logs params; search travels as `searchText`, not `search`. */
export const buildActivityLogsQueryParams = (state = {}) => {
  const params = buildListQueryParams(state, { sortFields: ACTIVITY_SORT_FIELDS });
  const searchText = state.search?.trim() || undefined;
  return withParam(
    withParam(params, 'searchText', searchText),
    'activityFilters',
    toJsonParam(buildActivityFilters(state)),
  );
};

const formatLoggedBy = (loggedBy) => {
  const name = loggedBy?.name?.trim();
  if (name && loggedBy.email) return `${name} (${loggedBy.email})`;
  return formatTextOrDash(name || loggedBy?.email);
};

/** One Activity log row; `description` and the CTA label are server-rendered copy. */
export const processActivityLogRow = (dto) => {
  const actionTo = resolveActivityLink(dto.ctaButton);
  return {
    id: dto.id,
    dateTime: formatDateTimeOrDash(dto.created_at),
    moduleCode: dto.activity_type ?? null,
    module: activityModuleLabel(dto.activity_type) ?? '-',
    user: formatLoggedBy(dto.logged_by),
    activity: formatTextOrDash(dto.description),
    actionLabel: actionTo ? dto.ctaButton.label : null,
    actionTo,
  };
};

export const processActivityLogsPage = (dto) => ({
  ...processListPage(dto, 'activity_logs', processActivityLogRow),
  isEmpty: Boolean(dto?.output?.isEmpty),
});
